// src/array/isEmptyArray.ts
function isEmptyArray(array) {
  return Array.isArray(array) && array.length === 0;
}

// src/array/isLastIndex.ts
function isLastIndex(index, array) {
  return Array.isArray(array) && index === array.length - 1;
}

// src/array/getLastItem.ts
function getLastItem(array) {
  return Array.isArray(array) ? array[array.length - 1] : void 0;
}

// src/array/unique.ts
function unique(array) {
  return Array.from(new Set(array));
}

// src/predicate/isDate.ts
function isDate(value) {
  return value instanceof Date || typeof value === "object" && Object.prototype.toString.call(value) === "[object Date]";
}

// src/predicate/isNil.ts
function isNil(value) {
  return value == null;
}

// src/predicate/isNotNil.ts
function isNotNil(value) {
  return value != null;
}

// src/predicate/isObject.ts
function isObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

// src/predicate/isBoolean.ts
function isBoolean(value) {
  return typeof value === "boolean" || value instanceof Boolean;
}

// src/predicate/isNumber.ts
function isNumber(value) {
  return typeof value === "number";
}

// src/predicate/isClient.ts
function isClient() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

// src/predicate/isServer.ts
function isServer() {
  return !isClient();
}

// src/predicate/isArray.ts
function isArray(value) {
  return Array.isArray(value);
}

// src/predicate/isFunction.ts
function isFunction(value) {
  return typeof value === "function";
}

// src/math/clamp.ts
function clamp(value, min, max) {
  if (isNil(max)) {
    return Math.min(value, min);
  }
  return Math.min(Math.max(value, min), max);
}

// src/math/random.ts
function random(min, max) {
  if (isNil(max)) {
    max = min;
    min = 0;
  }
  if (min >= max) {
    throw new Error(
      "Invalid input: The maximum value must be greater than the minimum value."
    );
  }
  return Math.random() * (max - min) + min;
}

// src/math/randomInt.ts
function randomInt(min, max) {
  return Math.floor(random(min, max));
}

// src/function/noop.ts
function noop() {
}

// src/string/_internal/getWords.ts
var CASE_SPLIT_PATTERN = /[A-Z]?[a-z]+|[0-9]+|[A-Z]+(?![a-z])|\p{Emoji_Presentation}|\p{Extended_Pictographic}|\p{L}+/gu;
function getWords(str) {
  return Array.from(str.match(CASE_SPLIT_PATTERN) ?? []);
}

// src/string/snakeCase.ts
function snakeCase(str) {
  const words = getWords(str);
  return words.map((word) => word.toLowerCase()).join("_");
}

// src/device/isMobileDevice.ts
function isMobileDevice() {
  if (isServer()) return false;
  const userAgent = window.navigator.userAgent;
  const deviceNameRegex = /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i;
  const deviceModelRegex = /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i;
  return deviceNameRegex.test(userAgent) || deviceModelRegex.test(userAgent.slice(0, 4));
}

// src/device/isMac.ts
function isMac() {
  if (isServer()) return false;
  return navigator.userAgent.toLowerCase().includes("mac");
}

// src/date/calculateAge.ts
function calculateAge(target, standard = /* @__PURE__ */ new Date()) {
  const age = standard.getUTCFullYear() - target.getUTCFullYear();
  const birthdayThisYear = new Date(
    Date.UTC(
      standard.getUTCFullYear(),
      target.getUTCMonth(),
      target.getUTCDate(),
      target.getUTCHours(),
      target.getUTCMinutes(),
      target.getUTCSeconds()
    )
  );
  return standard < birthdayThisYear ? age - 1 : age;
}

// src/date/isBeforeOrEqual.ts
function isBeforeOrEqual(date1, date2) {
  return date1.getTime() <= date2.getTime();
}

// src/date/isAfterOrEqual.ts
function isAfterOrEqual(date1, date2) {
  return date1.getTime() >= date2.getTime();
}

// src/date/getDateDiff.ts
function getDateDiff(startDate, endDate) {
  const SECOND_TO_MS = 1e3;
  const MINUTE_TO_MS = 1e3 * 60;
  const HOUR_TO_MS = 1e3 * 60 * 60;
  const DAY_TO_MS = 1e3 * 60 * 60 * 24;
  const endTime = endDate.getTime();
  const startTime = startDate.getTime();
  const diff = endTime - startTime;
  return {
    days: ~~(diff / DAY_TO_MS),
    hours: ~~(diff % DAY_TO_MS / HOUR_TO_MS),
    minutes: ~~(diff % HOUR_TO_MS / MINUTE_TO_MS),
    seconds: ~~(diff % MINUTE_TO_MS / SECOND_TO_MS)
  };
}

// src/date/_constants/index.ts
var MILLISECOND = 1;
var SECOND = MILLISECOND * 1e3;
var MINUTE = SECOND * 60;
var HOUR = MINUTE * 60;
var DAY = HOUR * 24;
var MONTH = DAY * 30;
var YEAR = MONTH * 12;

// src/date/getTimePastText.ts
function getTimePastText(date) {
  const now = /* @__PURE__ */ new Date();
  const targetDate = typeof date === "string" ? new Date(date) : date;
  const diffInMilliSeconds = now.getTime() - targetDate.getTime();
  const conditions = [
    { upperLimit: MINUTE, text: "\uBC29\uAE08 \uC804" },
    {
      upperLimit: HOUR,
      text: `${Math.floor(diffInMilliSeconds / MINUTE)}\uBD84 \uC804`
    },
    {
      upperLimit: DAY,
      text: `${Math.floor(diffInMilliSeconds / HOUR)}\uC2DC\uAC04 \uC804`
    },
    {
      upperLimit: MONTH,
      text: `${Math.floor(diffInMilliSeconds / DAY)}\uC77C \uC804`
    },
    {
      upperLimit: YEAR,
      text: `${Math.floor(diffInMilliSeconds / MONTH)}\uB2EC \uC804`
    }
  ];
  const target = conditions.find(
    (condition) => diffInMilliSeconds < condition.upperLimit
  );
  return target ? target.text : `${Math.floor(diffInMilliSeconds / YEAR)}\uB144 \uC804`;
}

// src/promise/delay.ts
function delay(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// src/number/formatPhoneNumber.ts
function formatPhoneNumber(value) {
  if (!value) {
    return value;
  }
  const digits = value.replace(/[^\d]/g, "");
  if (digits.length <= 3) {
    return digits;
  }
  return isSeoulNumber(digits) ? formatSeoulNumber(digits) : formatGeneralNumber(digits);
}
function isSeoulNumber(digits) {
  return digits.startsWith("02");
}
function formatSeoulNumber(digits) {
  const number = digits.slice(2);
  if (number.length <= 3) {
    return `02-${number}`;
  }
  if (number.length === 8) {
    return `02-${number.slice(0, 4)}-${number.slice(4)}`;
  }
  return `02-${number.slice(0, 3)}-${number.slice(3)}`;
}
function formatGeneralNumber(digits) {
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  if (digits.length <= 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
}
export {
  calculateAge,
  clamp,
  delay,
  formatPhoneNumber,
  getDateDiff,
  getLastItem,
  getTimePastText,
  isAfterOrEqual,
  isArray,
  isBeforeOrEqual,
  isBoolean,
  isClient,
  isDate,
  isEmptyArray,
  isFunction,
  isLastIndex,
  isMac,
  isMobileDevice,
  isNil,
  isNotNil,
  isNumber,
  isObject,
  isServer,
  noop,
  random,
  randomInt,
  snakeCase,
  unique
};
